import React from 'react';
import GenericSection from '../components/sections/GenericSection';
import SectionHeader from '../components/sections/partials/SectionHeader';
import FeaturesTiles from '../components/sections/FeaturesTiles';
import GlobeAnimation from '../components/elements/GlobeAnimation';
import Button from '../components/elements/Button';
import Image from '../components/elements/Image';
import { Link } from 'react-router-dom';

const Home = () => {
  const heroHeader = {
    title: 'Welcome to SEES INTERNATIONAL',
    paragraph:
      'A modern, US-headquartered global trade and enterprise solutions group operating at the intersection of international commerce, digital infrastructure, and strategic brokerage. Connecting high-growth industries across the Americas, Asia, Europe, and the Middle East & Africa (MEA).',
  };

  const tradeLines = [
    {
      num: '01',
      title: 'Global Reselling, Sourcing & Cross-Border Trade',
      bullets: [
        'Global Procurement Network: Direct sourcing of industrial materials, consumer goods, and raw commodities.',
        'Multi-Regional Supply Chain Management: End-to-end import/export workflows and customs navigation.',
        'Strategic Gateway Operations: Key UAE and North American ports for re-export and trade routing.'
      ]
    },
    {
      num: '02',
      title: 'FMCG & Agricultural Trade',
      bullets: [
        'Confectionery & Gourmet Goods: Global distribution of specialty foods, chocolates, and premium snacks.',
        'Spices & Agricultural Commodities: Import/export of high-grade spices, cardamom, chili varieties, and herbs.',
        'Global Compliance & Quality: Full adherence to FDA, regional food safety, and trade clearance standards.'
      ]
    },
    {
      num: '03',
      title: 'Industrial Coatings, Paints & Chemical Export',
      bullets: [
        'Global & Regional Procurement: Sourcing architectural, marine, and protective coatings from top producers.',
        'Cross-Border Wholesale: Direct contractor and wholesale supply of industrial paints, thinners, and base coats.',
        'Overland & Maritime Freight: GCC land-border exports and containerized ocean freight to Asia and Africa.'
      ]
    },
    {
      num: '04',
      title: 'B2B Commercial Brokerage & Intermediary Trade',
      bullets: [
        'High-Value Deal Structuring: Matching global buyers and suppliers across wholesale commodity markets.',
        'Agency Representation: Facilitating cross-border trade without locking client capital in warehousing.',
        'Risk-Shielded Sourcing: Secure contractual bridges for enterprise procurement across emerging markets.'
      ]
    }
  ];

  const techLines = [
    {
      num: '05',
      title: 'Multi-Channel E-Commerce & Marketplace Operations',
      bullets: [
        'Global Digital Portals: Online storefronts designed for international product discovery and wholesale ordering.',
        'Multi-Currency Merchant Ecosystems: Integrated transaction environments supporting multi-currency processing.',
        'Interactive Marketplace Architecture: Multi-vendor aggregators and commercial booking platforms.'
      ]
    },
    {
      num: '06',
      title: 'Enterprise Software Distribution & Asset Management',
      bullets: [
        'SaaS & Enterprise Reselling: Authorized distribution of software suites, enterprise OS, and business apps.',
        'Cloud & Mobile App Provisioning: Sourcing and deployment of applications for healthcare, finance, and logistics.',
        'Software Asset Management: Evaluating, acquiring, and maintaining optimized software licensing.'
      ]
    },
    {
      num: '07',
      title: 'On-Site IT Infrastructure & Hardware Deployment',
      bullets: [
        'Server & Network Deployment: Assembly and configuration of server racks, networks, and workstations.',
        'Infrastructure Cabling & Setup: Wiring, router setups, and system testing for corporate real estate.',
        'Rapid System Maintenance: On-site hardware troubleshooting, system recovery, and performance upgrades.'
      ]
    },
    {
      num: '08',
      title: 'Strategic IT Consultancy & System Architecture',
      bullets: [
        'Enterprise Tech Audits: Workflow evaluations to recommend and integrate custom software ecosystems.',
        'Digital Transformation Frameworks: Guiding migrations from legacy setups to cloud environments.',
        'Feasibility & Performance Analysis: Technical feasibility studies to maximize technology ROI.'
      ]
    },
    {
      num: '09',
      title: 'Digital Growth, Performance Marketing & B2B Lead Engine',
      bullets: [
        'Global Campaign Management: Execution across global advertising networks and business platforms.',
        'Qualified B2B Lead Blocks: Acquiring, verifying, and routing high-intent lead blocks for sales pipelines.',
        'Performance Marketing Engines: Conversion campaigns designed to maximize international penetration.'
      ]
    }
  ];

  return (
    <>
      {/* Hero Section with Logo, 3D Globe, and Dual CTAs */}
      <GenericSection topOuterDivider pb={0}>
        <div style={{ textAlign: 'center', margin: '0 auto 16px' }}>
          <Image
            src={require('../assets/images/sees_logo_gold.png')}
            alt="SEES INTERNATIONAL Emblem"
            style={{ maxHeight: '240px', width: 'auto', margin: '0 auto 16px', filter: 'drop-shadow(0 10px 30px rgba(168, 135, 86, 0.25))' }}
          />
        </div>

        {/* Interactive 3D Canvas Globe Component */}
        <GlobeAnimation />

        <div style={{ position: 'relative', zIndex: 10 }}>
          <SectionHeader data={heroHeader} className="center-content">
            <div style={{ marginTop: '40px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/trade">
                <Button color="primary">Explore Global Trade</Button>
              </Link>
              <Link to="/tech">
                <Button style={{ backgroundColor: '#141C2E', color: '#A88756', border: '1px solid #A88756' }}>
                  Explore Technology
                </Button>
              </Link>
            </div>
          </SectionHeader>
        </div>
      </GenericSection>

      {/* Corporate Overview & Global Gateway Banner */}
      <section className="container mt-32 mb-48">
        <div className="p-32" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B' }}>
          <h3 className="mt-0 mb-16 text-center" style={{ color: '#A88756', textAlign: 'center' }}>About SEES INTERNATIONAL</h3>
          <p className="text-sm mb-20" style={{ color: '#CBD5E1', lineHeight: '1.8' }}>
            Established in the <strong>United States in early 2021</strong>, <strong>SEES INTERNATIONAL</strong> has built a proven track record of executing high-value commercial trade and digital enterprise operations globally. Built on a foundation of global sourcing power, market intelligence, and multi-regional logistics pipelines, we serve as a single, high-trust gateway for enterprises looking to buy, sell, scale, or transform. From leveraging premier trade routes and major UAE maritime ports for seamless East-West distribution to deploying enterprise IT architecture, we deliver end-to-end operational solutions built for the modern global market.
          </p>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '16px', borderTop: '1px solid #1E293B' }}>
            <div style={{ flex: '1 min(200px, 100%)' }}>
              <h5 className="m-0 mb-4" style={{ color: '#F8FAFC', fontSize: '0.95rem' }}>🇺🇸 US HQ (Est. 2021)</h5>
              <p className="m-0 text-xs" style={{ color: '#94A3B8' }}>Corporate Governance & Compliance</p>
            </div>
            <div style={{ flex: '1 min(200px, 100%)' }}>
              <h5 className="m-0 mb-4" style={{ color: '#F8FAFC', fontSize: '0.95rem' }}>🇦🇪 MEA & UAE Trade Hubs</h5>
              <p className="m-0 text-xs" style={{ color: '#94A3B8' }}>Maritime Re-Export & Staging Ports</p>
            </div>
            <div style={{ flex: '1 min(200px, 100%)' }}>
              <h5 className="m-0 mb-4" style={{ color: '#F8FAFC', fontSize: '0.95rem' }}>🌏 Americas, Asia & MEA</h5>
              <p className="m-0 text-xs" style={{ color: '#94A3B8' }}>Cross-Border Distribution Corridors</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Lines Section Header */}
      <GenericSection topDivider>
        <div className="container center-content">
          <h2 className="mt-0 mb-12" style={{ color: '#A88756' }}>Our Business Lines & Core Capabilities</h2>
          <p className="text-sm text-color-high mb-32" style={{ maxWidth: '750px', margin: '0 auto 32px', color: '#CBD5E1' }}>
            Structured under two specialized operating divisions to serve global commercial procurement and digital infrastructure needs.
          </p>
        </div>

        {/* Division 1: Global Trade & Sourcing Group (4 Pillars: 2 on Top, 2 on Bottom) */}
        <div className="container mb-48">
          <div className="p-24 mb-24" style={{ backgroundColor: '#0A0F1D', borderRadius: '8px', borderLeft: '4px solid #A88756', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 className="m-0" style={{ color: '#F8FAFC', fontSize: '1.4rem' }}>Global Trade & Sourcing Group</h3>
              <p className="m-0 text-xs mt-4" style={{ color: '#94A3B8' }}>Cross-Border Commodities, Reselling, Coatings & Intermediary Brokerage</p>
            </div>
            <Link to="/trade">
              <Button color="primary" size="sm">Explore Trade Division →</Button>
            </Link>
          </div>

          {/* Top Row: Pillars 01, 02, 03 (3 Boxes Equally Spaced) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px', alignItems: 'stretch' }}>
            {tradeLines.slice(0, 3).map((line, idx) => (
              <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <div>
                  <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.9rem', marginBottom: '8px' }}>Pillar {line.num}</div>
                  <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.15rem' }}>{line.title}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {line.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                        • {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row: Pillar 04 (Spreads Across Entire Row / All 3 Columns) */}
          {tradeLines.slice(3, 4).map((line, idx) => (
            <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', width: '100%' }}>
              <div>
                <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.9rem', marginBottom: '8px' }}>Pillar {line.num}</div>
                <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.15rem' }}>{line.title}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {line.bullets.map((bullet, bIdx) => (
                    <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                      • {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Division 2: Technology & Digital Solutions Group (5 Pillars: 3 on Top Row, 2 on Bottom Row) */}
        <div className="container mb-48">
          <div className="p-24 mb-24" style={{ backgroundColor: '#0A0F1D', borderRadius: '8px', borderLeft: '4px solid #A88756', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 className="m-0" style={{ color: '#F8FAFC', fontSize: '1.4rem' }}>Technology & Digital Solutions Group</h3>
              <p className="m-0 text-xs mt-4" style={{ color: '#94A3B8' }}>E-Commerce Systems, Enterprise Software, Hardware Deployment & Growth Engines</p>
            </div>
            <Link to="/tech">
              <Button style={{ backgroundColor: '#141C2E', color: '#A88756', border: '1px solid #A88756' }} size="sm">
                Explore Tech Division →
              </Button>
            </Link>
          </div>

          {/* Top Row: 3 Boxes Equally Spaced */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px', alignItems: 'stretch' }}>
            {techLines.slice(0, 3).map((line, idx) => (
              <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <div>
                  <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.9rem', marginBottom: '8px' }}>Pillar {line.num}</div>
                  <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.15rem' }}>{line.title}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {line.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                        • {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row: 2 Boxes Equally Dividing 50%/50% */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
            {techLines.slice(3, 5).map((line, idx) => (
              <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <div>
                  <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.9rem', marginBottom: '8px' }}>Pillar {line.num}</div>
                  <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.15rem' }}>{line.title}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {line.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                        • {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </GenericSection>

      {/* Frequently Sourced Items CTA Section */}
      <section className="container mb-32">
        <div className="p-32 text-center" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B' }}>
          <h3 className="mt-0 mb-12" style={{ color: '#A88756' }}>Most Frequently Sourced Items</h3>
          <p className="text-sm mb-24" style={{ color: '#CBD5E1', maxWidth: '700px', margin: '0 auto 24px' }}>
            Browse our dynamically updated catalog of commercial equipment, medical items, and industrial supplies. For anything not in our list, click Contact Us to request custom sourcing.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/catalog">
              <Button color="primary">View Product Catalog →</Button>
            </Link>
            <Link to="/contact">
              <Button style={{ backgroundColor: '#0A0F1D', color: '#A88756', border: '1px solid #A88756' }}>
                Contact Us for Unlisted Items
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Core Industries on Demand (Moved right under Most Frequently Sourced Items) */}
      <FeaturesTiles topDivider title="Core Industries on Demand" />
    </>
  );
};

export default Home;
